import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { idiomaDeLaRuta } from './lib/rutasPublicas';

import translationES from './locales/es/translation.json';

/**
 * ── SOLO EL ESPAÑOL VA EN EL BUNDLE ─────────────────────────────────────────
 *
 * Los cinco `translation.json` pesan ~2.038 KB de fuente y estaban los cinco
 * dentro del chunk de entrada: el **45 %** de sus 4.546 KB. O sea que cada
 * visitante descargaba las traducciones de cuatro idiomas que no iba a leer, y
 * lo hacía antes de ver nada.
 *
 * El español se queda estático porque es el `fallbackLng`: tiene que estar
 * disponible de forma SÍNCRONA o, mientras llega el idioma de verdad, i18next
 * pintaría las claves en crudo en vez de caer al español. Los otros cuatro se
 * cargan bajo demanda.
 *
 * `import.meta.glob` y no un `import()` con plantilla: es lo que Vite entiende
 * de forma fiable en los DOS builds, el de cliente y el de SSR. Las llaves
 * `{en,pt,de,fr}` dejan fuera el español a propósito — si entrara, Vite emitiría
 * además un chunk con él que nadie llega a pedir nunca.
 */
const CARGADORES = import.meta.glob('./locales/{en,pt,de,fr}/translation.json');

const resources = { es: { translation: translationES } };

/**
 * Trae las traducciones de un idioma si no están ya.
 *
 * Es idempotente y barata en la segunda llamada: `hasResourceBundle` corta
 * antes de tocar la red, y el propio `import()` cachea el módulo.
 *
 * Si la descarga falla NO revienta: se queda sin ese idioma y i18next cae al
 * español, que es un fallo mucho más leve que una pantalla en blanco. Un chunk
 * que no carga suele ser un deploy a mitad, y para eso ya está `chunkReload`.
 */
export async function cargaIdioma(lang) {
  if (!lang || lang === 'es') return;
  if (i18n.hasResourceBundle(lang, 'translation')) return;

  const cargador = CARGADORES[`./locales/${lang}/translation.json`];
  if (!cargador) return;

  try {
    const modulo = await cargador();
    i18n.addResourceBundle(lang, 'translation', modulo.default, true, true);
  } catch (e) {
    console.warn(`[i18n] no se pudo cargar «${lang}», se usará el español`, e);
  }
}

/**
 * En las páginas públicas manda la URL, y por delante de todo lo demás.
 *
 * `/hoteles` es española y `/de/hotels` alemana venga quien venga: si no, cada
 * URL serviría cinco idiomas según el navegador del visitante y Google —que
 * renderiza con `en-US`— indexaría el sitio entero en inglés.
 *
 * Fuera de esas páginas devuelve `undefined` y la cadena sigue como siempre
 * (`localStorage` → `navigator`). Eso es lo que deja intacto el panel con
 * sesión: ahí no hay prefijo y el idioma lo sigue eligiendo el usuario.
 *
 * Esta cadena es solo la PRIMERA respuesta, síncrona. Antes de montar,
 * `lib/idiomaSesion.js` la corrige con lo que solo se sabe después: la
 * elección manual de esta sesión y el idioma que impone el país (IP de
 * Portugal → pt, que además lleva de la home española a /pt/). Y al navegar,
 * `IdiomaSegunRuta` aplica esa misma precedencia en las rutas sin prefijo.
 *
 * El detector `path` que trae el plugin NO vale: lee el primer segmento a
 * secas, así que en `/precios` devolvería «precios», y no sabe distinguir una
 * ruta pública española de una privada.
 */
const detector = new LanguageDetector();
detector.addDetector({
  name: 'rutaPublica',
  lookup() {
    // El guardia de `window` es para el prerender: este módulo se importa
    // también desde Node, donde no hay `location`.
    if (typeof window === 'undefined') return undefined;
    return idiomaDeLaRuta(window.location.pathname) ?? undefined;
  },
});

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    supportedLngs: ['es', 'en', 'pt', 'de', 'fr'],
    load: 'languageOnly',
    interpolation: {
      escapeValue: false // react already safes from xss
    },
    detection: {
      order: ['rutaPublica', 'localStorage', 'navigator', 'htmlTag', 'subdomain'],
      lookupLocalStorage: 'i18nextLng',
      /*
       * `caches: []` —antes `['localStorage']`— y NO es un descuido.
       *
       * El plugin cachea en CADA `changeLanguage`, incluido el que dispara la
       * detección inicial. Con la URL mandando, un visitante alemán que abra un
       * enlace a `/hoteles` vería español (correcto) y de paso se le
       * sobrescribiría su `i18nextLng` a «es» (no correcto): al entrar en el
       * panel lo tendría en español, y si se registrara desde ahí quedaría
       * guardado como cliente español —`Register.jsx` manda `preferredLanguage`
       * al alta—. Un idioma DETECTADO no es una preferencia.
       *
       * Ahora solo se guarda lo que el usuario elige a mano, y lo hace
       * `LanguageContext.setLanguage`, que es el único sitio por el que pasa esa
       * decisión. Leer `localStorage` sigue igual: `lookupLocalStorage` de aquí
       * arriba, y el loader de CookieYes en `index.html`.
       */
      caches: [],
      checkWhitelist: true
    }
  });

/**
 * Cada `changeLanguage` espera antes a que su idioma esté cargado.
 *
 * Se envuelve el método en vez de pedir a cada llamante que cargue primero
 * porque los llamantes son varios y de sitios muy distintos —
 * `LanguageContext.setLanguage`, el conmutador de la landing y el prerender en
 * `entry-server.jsx`— y basta olvidarse en uno para que esa pantalla salga en
 * español sin que nadie lo note.
 *
 * `changeLanguage` ya devolvía una promesa y ya se esperaba donde importaba, así
 * que esto no cambia ningún contrato: solo hace que la promesa tarde un poco más
 * la primera vez que se pide un idioma.
 */
const cambiarIdiomaOriginal = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lang, ...resto) => {
  await cargaIdioma(lang);
  return cambiarIdiomaOriginal(lang, ...resto);
};

/**
 * El idioma DETECTADO al arrancar, ya cargado.
 *
 * `init()` resuelve la detección por su cuenta y sin pasar por el
 * `changeLanguage` público, así que el idioma inicial se queda fuera del envoltorio
 * de arriba y hay que cargarlo aparte. `main.jsx` espera a esta promesa antes de
 * montar.
 *
 * Esperar no deja al visitante mirando una pantalla en blanco: el HTML
 * prerenderizado ya está pintado, en su idioma y con su contenido, y lo que se
 * está esperando es solo a poder reemplazarlo por el árbol de React. Sin la
 * espera, un visitante alemán vería su página correcta, luego un parpadeo en
 * español y luego alemán otra vez.
 */
export const i18nListo = cargaIdioma(i18n.language);

export default i18n;
